import React, {useState, useEffect} from 'react'
import {Modal} from 'react-bootstrap'
import {Button, Select, TextInput, Textarea} from '@mantine/core'
import {toast} from 'react-toastify'
import {
  getProjectCodes,
  createProjectCode,
  updateProjectCode,
  deleteProjectCode,
  activateProjectCode,
  ProjectCode,
  ProjectCodeInput,
} from '../../../api/TimesheetAPI'
import {PageHeader} from '../../components/PageHeader/PageHeader'
import {DataTable} from '../../components/DataTable/DataTable'
import {ActionButtons} from '../../components/ActionButtons/ActionButtons'
import {StatusBadge} from '../../components/StatusBadge/StatusBadge'
import {FormWrapper} from '../../components/FormWrapper/FormWrapper'
import {useApiCall} from '../../hooks/useApiCall'

const ProjectCodes: React.FC = () => {
  const [projectCodes, setProjectCodes] = useState<ProjectCode[]>([])
  const [filteredCodes, setFilteredCodes] = useState<ProjectCode[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCode, setEditingCode] = useState<ProjectCode | null>(null)
  const [typeFilter, setTypeFilter] = useState<string>('ALL')
  const [statusFilter, setStatusFilter] = useState<string>('ALL')

  const {loading, error, execute} = useApiCall<any>()

  const [formData, setFormData] = useState<ProjectCodeInput>({
    code: '',
    name: '',
    description: '',
    type: 'INTERNAL',
    isActive: true,
  })

  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  // Load project codes
  const loadProjectCodes = async () => {
    await execute(async () => {
      const response = await getProjectCodes()
      setProjectCodes(response.data.data)
      return response.data.data
    })
  }

  useEffect(() => {
    loadProjectCodes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Apply filters
  useEffect(() => {
    let filtered = projectCodes

    if (typeFilter !== 'ALL') {
      filtered = filtered.filter((code) => code.type === typeFilter)
    }

    if (statusFilter === 'ACTIVE') {
      filtered = filtered.filter((code) => code.isActive)
    } else if (statusFilter === 'INACTIVE') {
      filtered = filtered.filter((code) => !code.isActive)
    }

    setFilteredCodes(filtered)
  }, [projectCodes, typeFilter, statusFilter])

  const openModal = (code?: ProjectCode) => {
    if (code) {
      setEditingCode(code)
      setFormData({
        code: code.code,
        name: code.name,
        description: code.description || '',
        type: code.type,
        isActive: code.isActive,
      })
    } else {
      setEditingCode(null)
      setFormData({
        code: '',
        name: '',
        description: '',
        type: 'INTERNAL',
        isActive: true,
      })
    }
    setFormErrors({})
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingCode(null)
    setFormData({
      code: '',
      name: '',
      description: '',
      type: 'INTERNAL',
      isActive: true,
    })
    setFormErrors({})
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (!formData.code.trim()) {
      errors.code = 'Code is required'
    } else if (formData.code.length < 2) {
      errors.code = 'Code must be at least 2 characters'
    }

    if (!formData.name.trim()) {
      errors.name = 'Name is required'
    } else if (formData.name.length < 3) {
      errors.name = 'Name must be at least 3 characters'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    await execute(async () => {
      if (editingCode) {
        const response = await updateProjectCode(editingCode.id, formData)
        toast.success('Project code updated successfully')
        return response.data
      } else {
        const response = await createProjectCode(formData)
        toast.success('Project code created successfully')
        return response.data
      }
    })

    closeModal()
    loadProjectCodes()
  }

  const handleDelete = async (code: ProjectCode) => {
    if (!window.confirm(`Are you sure you want to delete "${code.name}"?`)) {
      return
    }

    await execute(async () => {
      const response = await deleteProjectCode(code.id)
      toast.success(response.data.message || 'Project code deleted successfully')
      return response.data
    })

    loadProjectCodes()
  }

  const handleActivate = async (code: ProjectCode) => {
    await execute(async () => {
      const response = await activateProjectCode(code.id)
      toast.success('Project code activated successfully')
      return response.data
    })

    loadProjectCodes()
  }

  const columns = [
    {
      header: 'Code',
      cell: (row: ProjectCode) => <strong>{row.code}</strong>,
    },
    {
      header: 'Name',
      cell: (row: ProjectCode) => row.name,
    },
    {
      header: 'Type',
      cell: (row: ProjectCode) => (
        <span
          className={`badge ${
            row.type === 'INTERNAL' ? 'badge-light-primary' : 'badge-light-success'
          }`}
        >
          {row.type}
        </span>
      ),
    },
    {
      header: 'Description',
      cell: (row: ProjectCode) => (
        <span className='text-muted'>{row.description || '-'}</span>
      ),
    },
    {
      header: 'Status',
      cell: (row: ProjectCode) => (
        <StatusBadge status={row.isActive ? 'Active' : 'Inactive'} />
      ),
    },
    {
      header: 'Actions',
      cell: (row: ProjectCode) => (
        <div className='d-flex gap-2'>
          <ActionButtons
            actions={[
              {
                type: 'edit',
                onClick: () => openModal(row),
                label: 'Edit',
              },
              ...(row.isActive ? [{
                type: 'delete' as const,
                onClick: () => handleDelete(row),
                label: 'Delete',
                confirm: {
                  title: 'Delete Project Code',
                  text: `Are you sure you want to delete "${row.name}"?`,
                  icon: 'warning' as const,
                },
              }] : []),
            ]}
          />
          {!row.isActive && (
            <Button size='xs' color='green' onClick={() => handleActivate(row)}>
              Activate
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <>
      <div className='card card-flush shadow-sm'>
        <div className='card-header'>
          <h3 className='card-title'>Project Codes Management</h3>
          <div className='card-toolbar'>
            <Button onClick={() => openModal()}>
              Add Project Code
            </Button>
          </div>
        </div>
        <div className='card-body'>
        {/* Filters */}
        <div className='row mb-4'>
          <div className='col-md-3'>
            <Select
              label='Type'
              value={typeFilter}
              onChange={(value) => setTypeFilter(value || 'ALL')}
              data={[
                {value: 'ALL', label: 'All Types'},
                {value: 'INTERNAL', label: 'Internal'},
                {value: 'REAL', label: 'Real Projects'},
              ]}
            />
          </div>
          <div className='col-md-3'>
            <Select
              label='Status'
              value={statusFilter}
              onChange={(value) => setStatusFilter(value || 'ALL')}
              data={[
                {value: 'ALL', label: 'All Status'},
                {value: 'ACTIVE', label: 'Active Only'},
                {value: 'INACTIVE', label: 'Inactive Only'},
              ]}
            />
          </div>
          <div className='col-md-6 d-flex align-items-end'>
            <div className='text-muted'>
              Showing {filteredCodes.length} of {projectCodes.length} project codes
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className='alert alert-danger' role='alert'>
            {error}
          </div>
        )}

        {/* Table */}
        <DataTable
          data={filteredCodes}
          columns={columns}
          loading={loading}
          emptyMessage='No project codes found. Click "Add Project Code" to create one.'
        />
        </div>
      </div>

      {/* Add/Edit Modal */}
      {/* <Modal show={isModalOpen} onHide={closeModal} size='lg'> */}
      {/* <Modal
        show={isModalOpen}
        onHide={closeModal}
        size='lg'
        backdrop="static"
        keyboard={false}
      > */}
        {/* <FormWrapper
          title={editingCode ? 'Edit Project Code' : 'Add Project Code'}
          show={isModalOpen}
          onSubmit={handleSubmit}
          onCancel={closeModal}
          loading={loading}
        >
          <div className='row'>
            <div className='col-md-6'>
              <TextInput
                label='Code'
                placeholder='e.g., I0991, P001'
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value})}
                error={formErrors.code}
                required
                disabled={!!editingCode}
              />
            </div>
            <div className='col-md-6'>
              <Select
                label='Type'
                value={formData.type}
                onChange={(value) =>
                  setFormData({...formData, type: (value as 'INTERNAL' | 'REAL') || 'INTERNAL'})
                }
                data={[
                  {value: 'INTERNAL', label: 'Internal'},
                  {value: 'REAL', label: 'Real Project'},
                ]}
                required
                withinPortal={false}
              />
            </div>
          </div>

          <TextInput
            label='Name'
            placeholder='e.g., Learning & Development: L&D'
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            error={formErrors.name}
            required
            mt='md'
          />

          <Textarea
            label='Description'
            placeholder='Brief description of this project code'
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            rows={3}
            mt='md'
          />

          {editingCode && (
            <Select
              label='Status'
              value={formData.isActive ? 'true' : 'false'}
              onChange={(value) =>
                setFormData({...formData, isActive: value === 'true'})
              }
              data={[
                {value: 'true', label: 'Active'},
                {value: 'false', label: 'Inactive'},
              ]}
              mt='md'
            />
          )}
        </FormWrapper> */}
        <FormWrapper
        title={editingCode ? 'Edit Project Code' : 'Add Project Code'}
        show={isModalOpen}
        onSubmit={handleSubmit}
        onCancel={closeModal}
        loading={loading}
      >
        <div className='row'>
          <div className='col-md-6'>
            <TextInput
              label='Code'
              value={formData.code}
              onChange={(e) => setFormData({...formData, code: e.target.value})}
              disabled={!!editingCode}
              required
            />
          </div>

          <div className='col-md-6'>
            <Select
              label='Type'
              value={formData.type}
              onChange={(value) =>
                setFormData({...formData, type: value as 'INTERNAL' | 'REAL'})
              }
              data={[
                {value: 'INTERNAL', label: 'Internal'},
                {value: 'REAL', label: 'Real Project'},
              ]}
              withinPortal={false}
            />
          </div>
        </div>

        <TextInput
          label='Name'
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          required
          mt="md"
        />

        <Textarea
          label='Description'
          value={formData.description}
          onChange={(e) =>
            setFormData({...formData, description: e.target.value})
          }
          mt="md"
        />

        <Select
          label='Status'
          value={formData.isActive ? 'true' : 'false'}
          onChange={(value) =>
            setFormData({...formData, isActive: value === 'true'})
          }
          data={[
            {value: 'true', label: 'Active'},
            {value: 'false', label: 'Inactive'},
          ]}
          mt="md"
          withinPortal={false}
        />
      </FormWrapper>
    </>
  )
}

export default ProjectCodes

